import React, {Fragment, useEffect, useState, useCallback} from 'react';
import {withApollo} from '@apollo/react-hoc';
import formatMessage from '../../../utils/formatMessage';
import Modal from '@homeaway/react-modal';
import {createMultiUnitSearchQuery, setBaseVariables, setVariables} from '../utils/multiUnitQuery';
import MultiUnitTypesSummary from '../MultiUnitTypesSummary/MultiUnitTypesSummary';
import MultiUnitLoader from '../MultiUnitLoader/MultiUnitLoader';
import MultiUnitListing from '../MultiUnitListing/MultiUnitListing';
import {prepareMultiUnitListState} from '../utils/multiUnitHitHelper';
import ErrorMessage from '../../SharedComponents/ErrorMessage/ErrorMessage';

import './MultiUnitListWrapper.less';

const wrapWithModal = ({
    content,
    loading,
    resultCount,
    isOpen,
    onModalClose,
    propertyName,
    minPriceData,
    messages
}) => {
    return (
        <Modal
            className="MultiUnit__modal"
            id="multiUnitModal"
            isOpen={isOpen}
            onClose={onModalClose}
            modalAnimation="modal-from-bottom"
            isWorkflow
            aria-label={propertyName}
            title={
                <MultiUnitTypesSummary
                    resultCount={resultCount}
                    propertyName={propertyName}
                    loading={loading}
                    minPriceData={minPriceData}
                    messages={messages}
                />
            }
        >
            <ul className="MultiUnitModal__content">{content}</ul>
        </Modal>
    );
};

const multiUnitList = ({
    loading,
    propertyName,
    listings,
    unfilteredListings,
    resultCount,
    unfilteredResultCount,
    isDatelessSearch,
    isMetric,
    propertyId,
    showDesktopView,
    context,
    minPriceData,
    clientLogger,
    messages
}) => {
    return (
        <div className="MultiUnitListWrapper">
            {loading && (
                <Fragment>
                    {showDesktopView && (
                        <MultiUnitTypesSummary
                            propertyName={propertyName}
                            messages={messages}
                            loading
                        />
                    )}
                    <MultiUnitLoader multiUnitDataCount={showDesktopView ? 4 : 8} />
                </Fragment>
            )}
            {!!listings.length && (
                <div>
                    {showDesktopView && (
                        <MultiUnitTypesSummary
                            propertyName={propertyName}
                            resultCount={resultCount}
                            messages={messages}
                            loading={false}
                            minPriceData={minPriceData}
                        />
                    )}
                    <MultiUnitListing
                        context={context}
                        isDatelessSearch={isDatelessSearch}
                        isMetric={isMetric}
                        multiUnitList={{
                            multiUnitSearch: {listings}
                        }}
                        propertyId={propertyId}
                        clientLogger={clientLogger}
                        messages={messages}
                    />
                    {!!unfilteredListings.length && (
                        <Fragment>
                            <hr />
                            <span className="MultiUnitListWrapper__unfiltered-results-title micro">
                                {formatMessage(messages.multipleUnitsNotMatch, {
                                    count: unfilteredResultCount
                                })}
                            </span>
                            <MultiUnitListing
                                context={context}
                                isDatelessSearch={isDatelessSearch}
                                isMetric={isMetric}
                                multiUnitList={{
                                    multiUnitSearch: {
                                        listings: unfilteredListings
                                    }
                                }}
                                propertyId={propertyId}
                                clientLogger={clientLogger}
                                messages={messages}
                            />
                        </Fragment>
                    )}
                </div>
            )}
        </div>
    );
};

const initialState = {
    listings: [],
    loading: true,
    resultCount: 0,
    unfilteredResultCount: 0,
    unfilteredListings: [],
    minPriceData: {
        isPriceRange: false,
        price: '$0'
    }
};

const MultiUnitListWrapper = ({
    client,
    currency,
    inputParams,
    listing,
    clientLogger,
    isMetric,
    isDatelessSearch,
    propertyName,
    showDesktopView,
    isOpen,
    onDropdownHide,
    onModalClose,
    context,
    messages
}) => {
    const {listingNamespace: systemId, listingNumber: propertyId} = listing;
    const [isError, setIsError] = useState(false);
    const [data, setData] = useState(initialState);

    const fetchMultiUnitListings = useCallback(() => {
        const query = createMultiUnitSearchQuery(currency);

        Promise.all([
            client.query({
                query,
                variables: setVariables(inputParams, systemId, propertyId),
                errorPolicy: 'all'
            }),
            client.query({
                query,
                variables: setBaseVariables(systemId, propertyId),
                errorPolicy: 'all'
            })
        ])
            .then((responses) => {
                if (
                    responses.every(
                        (collection) => !collection.data.multiUnitSearch.listings.length
                    )
                ) {
                    clientLogger.captureException('[MultiUnitSearchQuery error]: No data', {
                        extra: `Unable to fetch listing results from graphql-traveler-search for property Id - ${propertyId}`
                    });
                    setIsError(true);
                } else {
                    setData(prepareMultiUnitListState(responses, listing, inputParams));
                }
            })
            .catch((e) => {
                setIsError(true);
                clientLogger.captureException(`[MultiUnitSearchQuery error]: ${e}`, {
                    extra: `Unable to fetch listing results from graphql-traveler-search for property Id - ${propertyId}`
                });
            });
    }, [client, clientLogger, currency, inputParams, listing, propertyId, systemId]);

    useEffect(() => fetchMultiUnitListings(), [fetchMultiUnitListings]);

    const {
        listings,
        resultCount,
        unfilteredListings,
        unfilteredResultCount,
        loading,
        minPriceData
    } = data;

    if (isError) {
        return showDesktopView ? (
            <ErrorMessage onClose={onDropdownHide} messages={messages} />
        ) : (
            wrapWithModal({
                isOpen,
                resultCount,
                loading: false,
                onModalClose,
                propertyName,
                minPriceData,
                content: <ErrorMessage onClose={onModalClose} messages={messages} />
            })
        );
    }

    const content = multiUnitList({
        listings,
        resultCount,
        unfilteredListings,
        unfilteredResultCount,
        loading,
        isMetric,
        isDatelessSearch,
        propertyId,
        propertyName,
        showDesktopView,
        context,
        minPriceData,
        clientLogger,
        messages
    });

    return showDesktopView
        ? content
        : wrapWithModal({
              isOpen,
              resultCount,
              loading,
              onModalClose,
              propertyName,
              content,
              minPriceData,
              messages
          });
};

export default withApollo(MultiUnitListWrapper);
